import { useCallback, useContext, useSyncExternalStore } from "react"

import { QueryClientContext } from "@tanstack/react-query"

import type { MyUsage } from "@/api/fair-use"

import { queryKeys } from "@/lib/query-keys"

const [USAGE_SCOPE, USAGE_KIND] = queryKeys.fairUseMyUsage

/**
 * Read the cached fair-use snapshot WITHOUT owning the fetch, and without
 * requiring a QueryClientProvider.
 *
 * Both properties matter for the composer. `useMyUsage` (the fetching hook) is
 * owned by `UsageIndicator`, which is mounted in the same composer row — a
 * second `useQuery` on the same key would be redundant, and a fetch triggered
 * from the footer would also fire on surfaces where the indicator is hidden.
 * And `useQuery` throws outright when no client is in context, which the
 * composer cannot assume: it renders in standalone harnesses (component tests,
 * isolated panels) with no provider, which is exactly why `UsageIndicator`
 * guards on `QueryClientContext` before mounting its own content. A hook can't
 * be called conditionally, so this subscribes to the query cache through
 * `useSyncExternalStore` instead and degrades to `undefined` — i.e. "no gate"
 * — when there is no client.
 *
 * Reactive to every write the fair-use bridge makes (live WS/durable metadata,
 * a block error's usage payload, the post-request refetch), because those all
 * land in this same cache entry.
 */
export function useMyUsageSnapshot(): MyUsage | undefined {
  const queryClient = useContext(QueryClientContext)

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!queryClient) return () => {}
      return queryClient.getQueryCache().subscribe((event) => {
        const [scope, kind] = event.query.queryKey
        if (scope !== USAGE_SCOPE || kind !== USAGE_KIND) return
        onStoreChange()
      })
    },
    [queryClient]
  )

  // `getQueryData` returns the very object held in the cache, so an unchanged
  // cache yields a referentially identical snapshot and
  // `useSyncExternalStore` does not re-render — which is what makes it safe to
  // call on every cache event above.
  const getSnapshot = useCallback(
    () => queryClient?.getQueryData<MyUsage>(queryKeys.fairUseMyUsage),
    [queryClient]
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Whether sending must be gated for this snapshot.
 *
 * ONLY the hard block (`status: "block"`) gates. `warn`, the request CTA and
 * `downgrade` are all states in which the user can — and must be able to —
 * keep working: `downgrade` in particular means answers are served by the
 * budget model, not that they are refused, and blocking there would take the
 * product away from someone the policy deliberately kept working.
 *
 * `enabled` is required too: with fair use switched off for the environment a
 * stale `block` left in the cache must not lock a composer no backend would
 * refuse.
 */
export function isFairUseSendBlocked(usage: MyUsage | undefined): boolean {
  return usage?.enabled === true && usage.status === "block"
}
